/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Card, Grid, TextField } from '@mui/material';
import { ChipStatus, DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { ApplicationDTO, ApplicationFilterDTO, DepartmentDTO, FeatureDTO, FeatureFilterDTO, IntegrationDTO, IntegrationSettingDTO, UserDTO, ValueListDTO } from '../../../shared/interfaces';
import { dateDiff, formatDateTime } from '../../../shared/utils';
import { isJsonString } from '../../../shared/utils';
import { tr } from 'date-fns/locale';
import ReactJson from 'react-json-view';

interface IIntegrationSettingMaintenanceProps {
    data: any;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const IntegrationSettingMaintenance: React.FC<IIntegrationSettingMaintenanceProps> = ({ data, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {

    const [flowList, setFlowList] = useState<ValueListDTO[]>([]);
    const [integrationTypeList, setIntegrationTypeList] = useState<ValueListDTO[]>([]);
    const [userList, setUserList] = useState<UserDTO[]>([]);
    const [sourceApplicationList, setSourceApplicationList] = useState<ApplicationDTO[]>([]);
    const [sourceFeatureList, setSourceFeatureList] = useState<FeatureDTO[]>([]);
    const [targetApplicationList, setTargetApplicationList] = useState<ApplicationDTO[]>([]);
    const [targetFeatureList, setTargetFeatureList] = useState<FeatureDTO[]>([]);
    const [productList, setProductList] = useState<ValueListDTO[]>([]);
    const [departmentList, setDepartmentList] = useState<DepartmentDTO[]>([]);

    const [integrationSettingDTO, setIntegrationSettingDTO] = useState<IntegrationSettingDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenIntegrationSetting, setIsOpenIntegrationSetting] = useState(false);

    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setIntegrationSettingDTO({
            ...integrationSettingDTO,
            id: '',
            name: '',
            description: '',
            flowId: '',
            integrationTypeId: '',
            sourceApplicationId: '',
            sourceFeatureId: '',
            targetApplicationId: '',
            targetFeatureId: '',
            productId: '',
            department: '',
            retriesAmount: 0,
            timeMillisecondsBetweenRetries: 0,
            integrationGroupId: '',
            sequence: 1,
        } as IntegrationSettingDTO);

        if(isOpenMaintenance){
            handleListGetAll(ListId.linksphere.integrationSetting.flowId, setFlowList);
            handleListGetAll(ListId.linksphere.integrationSetting.integrationTypeId, setIntegrationTypeList);
            handleUserGetAllAsync();
            handleApplicationGetAllAsync(setSourceApplicationList);
            handleApplicationGetAllAsync(setTargetApplicationList);
            handleDepartmentGetAllAsync();
            handleProductaGetAllAsync();
            handleGetIntegrationSettingByIdAsync();
        }
    }, [data, isOpenMaintenance]);

    useEffect(() => {
        if(isOpenMaintenance)
            handleFeatureGetAllAsync(setSourceFeatureList, integrationSettingDTO?.sourceApplicationId || '');
    }, [integrationSettingDTO?.sourceApplicationId]);

    useEffect(() => {
        if(isOpenMaintenance)
            handleFeatureGetAllAsync(setTargetFeatureList, integrationSettingDTO?.targetApplicationId || '');
    }, [integrationSettingDTO?.targetApplicationId]);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1('parmsphere.valueList', valueListFilterDTO, setLoading, setList, showMessage);
    }, []);

    const handleUserGetAllAsync = useCallback(async () => {
        const userFilterDTO = {
            IntegrationSettingId: userInfo.user.IntegrationSettingId,
        };
        GenericService.GetAllViewAsync1('authsphere.user', userFilterDTO, setActionExecuted, setLoading, setUserList, showMessage, false);

    }, []);

    const handleApplicationGetAllAsync = useCallback(async (setList: any) => {
        const applicationFilterDTO: ApplicationFilterDTO = {
            name: '',
            typeId: '',
            companyId: '',
        };
        GenericService.GetAllAsync1('appsphere.application', applicationFilterDTO, setLoading, setList, showMessage);
    }, []);
    
    const handleFeatureGetAllAsync = useCallback(async (setList: any, applicationId: string) => {
        const featureFilterDTO: FeatureFilterDTO = {
            name: '',
            applicationId: applicationId,
        };
        GenericService.GetAllAsync1('appsphere.feature', featureFilterDTO, setLoading, setList, showMessage);
    }, []);
    
    const handleDepartmentGetAllAsync = useCallback(async () => {
        const filterDTO = {
            name: '',
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('corpsphere.department', filterDTO, setLoading, setDepartmentList, showMessage);
    }, []);

    const handleProductaGetAllAsync = useCallback(async () => {
        const filterDTO = {
            name: '',
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('corpsphere.product', filterDTO, setLoading, setProductList, showMessage);
    }, []);

    const handleGetIntegrationSettingByIdAsync = useCallback(async () => {
        GenericService.GetByIdAsync1('linksphere.integrationSetting', data.id, setLoading, setIntegrationSettingDTO, showMessage);
    }, [data]);

    const handleSaveClick = useCallback(async () => {
        const dto = integrationSettingDTO as IntegrationSettingDTO;

        if (integrationSettingDTO?.id === '' || integrationSettingDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            GenericService.AddAsync1('linksphere.integrationSetting', dto, setIntegrationSettingDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('linksphere.integrationSetting', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [integrationSettingDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'integrationSetting',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenIntegrationSetting(true);
    }

    const actions: IDialogActions[] = [
        { id: 'save', text: 'Save', icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick },
        { id: 'cancel', text: 'Cancel', icon: 'cancel_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenIntegrationSetting && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenIntegrationSetting}
                    isOpen={isOpenIntegrationSetting}
                />
            )}
            <DialogForm
                title='INTEGRATION SETTING DETAILS'
                icon='integration_instructions_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                value={integrationSettingDTO?.name}
                                setValue={setIntegrationSettingDTO}
                                obj={integrationSettingDTO}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='flowId'
                                label='Flow'
                                setValue={setIntegrationSettingDTO}
                                value={integrationSettingDTO?.flowId}
                                obj={integrationSettingDTO}
                                valueList={flowList}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='integrationTypeId'
                                label='Integration Type'
                                setValue={setIntegrationSettingDTO}
                                value={integrationSettingDTO?.integrationTypeId}
                                obj={integrationSettingDTO}
                                valueList={integrationTypeList}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SelectField
                                id='sourceApplicationId'
                                label='Source Application'
                                setValue={setIntegrationSettingDTO}
                                value={integrationSettingDTO?.sourceApplicationId}
                                obj={integrationSettingDTO}
                                valueList={sourceApplicationList}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SelectField
                                id='sourceFeatureId'
                                label='Source Feature'
                                setValue={setIntegrationSettingDTO}
                                value={integrationSettingDTO?.sourceFeatureId}
                                obj={integrationSettingDTO}
                                valueList={sourceFeatureList}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SelectField
                                id='targetApplicationId'
                                label='Target Application'
                                setValue={setIntegrationSettingDTO}
                                value={integrationSettingDTO?.targetApplicationId}
                                obj={integrationSettingDTO}
                                valueList={targetApplicationList}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <SelectField
                                id='targetFeatureId'
                                label='Target Feature'
                                setValue={setIntegrationSettingDTO}
                                value={integrationSettingDTO?.targetFeatureId}
                                obj={integrationSettingDTO}
                                valueList={targetFeatureList}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='departmentId'
                                label='Department'
                                setValue={setIntegrationSettingDTO}
                                value={integrationSettingDTO?.departmentId}
                                obj={integrationSettingDTO}
                                valueList={departmentList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='productId'
                                label='Product'
                                setValue={setIntegrationSettingDTO}
                                value={integrationSettingDTO?.productId}
                                obj={integrationSettingDTO}
                                valueList={productList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='notificationSettingId'
                                label='Notification Setting'
                                setValue={setIntegrationSettingDTO}
                                value={integrationSettingDTO?.notificationSettingId}
                                obj={integrationSettingDTO}
                                valueList={departmentList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='integrationGroupId'
                                label='Integration Group'
                                setValue={setIntegrationSettingDTO}
                                value={integrationSettingDTO?.integrationGroupId}
                                obj={integrationSettingDTO}
                                valueList={departmentList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='retriesAmount'
                                label='Retries Amount'
                                value={integrationSettingDTO?.retriesAmount}
                                setValue={setIntegrationSettingDTO}
                                obj={integrationSettingDTO}
                                numericOnly={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='timeMillisecondsBetweenRetries'
                                label='Time Milliseconds Between Retries'
                                value={integrationSettingDTO?.timeMillisecondsBetweenRetries}
                                setValue={setIntegrationSettingDTO}
                                obj={integrationSettingDTO}
                                numericOnly={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='sequence'
                                label='Sequence'
                                value={integrationSettingDTO?.sequence}
                                setValue={setIntegrationSettingDTO}
                                obj={integrationSettingDTO}
                                numericOnly={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='description'
                                label='Description'
                                value={integrationSettingDTO?.description}
                                setValue={setIntegrationSettingDTO}
                                obj={integrationSettingDTO}
                                multiline={true}
                                rows={2}
                            />
                        </Grid>
                        {integrationSettingDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label='Id'
                                        value={integrationSettingDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label='Created By'
                                        value={userList.find(x => x.id === integrationSettingDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label='Creation Date'
                                        value={formatDateTime(String(integrationSettingDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label='Updated By'
                                        value={userList.find(x => x.id === integrationSettingDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label='Update Date'
                                        value={formatDateTime(String(integrationSettingDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm>
        </React.Fragment>
    );
};